import { useEffect, useState } from "react";
import { useAudioPlayback, useRoomContext } from "@livekit/components-react";
import { RoomEvent, type Room } from "livekit-client";

// Safari grants audio only during certain events -- click, mouse and key up
// and down, touchend -- and not reliably during pointer events, so listen to
// all of them; startAudio is harmless to repeat.
const GESTURES = [
  "pointerdown",
  "pointerup",
  "mousedown",
  "mouseup",
  "click",
  "keydown",
  "keyup",
  "touchend",
] as const;

/** How long an automatic resume may take before the prompt shows (AC5–AC7:
 *  audio is back within 1 s, or the user is asked for the tap). */
const RESUME_GRACE_MS = 700;

/** The context LiveKit mixes call audio into (private on Room). */
function mixContext(room: Room): AudioContext | undefined {
  return (room as unknown as { audioContext?: AudioContext }).audioContext;
}

/**
 * Turn call audio back on with the first gesture after the browser paused it.
 *
 * Browsers only start audio after a user gesture, and they block each audio
 * element on its own. So a call joined without a click -- the refresh-resume
 * after a reload -- plays nothing, and on Safari even a call joined with a
 * click does: LiveKit plays each voice seconds after the click, once
 * connected, which Safari refuses unless the page is capturing the mic. That
 * is why unmuting used to bring the sound back. A click later only helps
 * the voices whose audio starts after it, so a listener hears some people
 * and not others. `room.startAudio()` resumes the shared AudioContext and
 * every remote element at once, but must run inside a gesture, so this
 * calls it from the next one, anywhere on the page.
 *
 * It also brings audio back *without* a gesture after the device paused it
 * mid-call (#2223 AC5–AC7): backgrounding, lock/unlock, a phone call or Siri
 * suspend or interrupt the mix context on phones, and livekit-client never
 * resumes it. Whenever the page becomes visible again, the room reconnects or
 * the context leaves "running", this resumes it; only if the device refuses
 * (still not running after RESUME_GRACE_MS) does the prompt appear.
 *
 * Returns whether audio is still blocked, for a visible prompt -- someone who
 * only listens may never click anything.
 */
export function useAudioPlaybackUnlock(): { blocked: boolean; unlock: () => void } {
  const room = useRoomContext();
  const { canPlayAudio, startAudio } = useAudioPlayback(room);
  const [contextBlocked, setContextBlocked] = useState(false);

  useEffect(() => {
    let bound: AudioContext | undefined;
    let grace: ReturnType<typeof setTimeout> | undefined;
    const visible = () => document.visibilityState === "visible";

    const resume = () => {
      const context = mixContext(room);
      if (context !== bound) {
        bound?.removeEventListener("statechange", resume);
        bound = context;
        bound?.addEventListener("statechange", resume);
      }
      if (!context) return;
      // "interrupted" is iOS-only and missing from the DOM typings.
      if ((context.state as string) === "running") {
        clearTimeout(grace);
        setContextBlocked(false);
        return;
      }
      if (context.state === "closed" || !visible()) return;
      void context.resume().catch(() => {});
      clearTimeout(grace);
      grace = setTimeout(() => {
        setContextBlocked(visible() && (context.state as string) !== "running");
      }, RESUME_GRACE_MS);
    };

    resume();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    room.on(RoomEvent.Connected, resume);
    room.on(RoomEvent.Reconnected, resume);
    return () => {
      clearTimeout(grace);
      bound?.removeEventListener("statechange", resume);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      room.off(RoomEvent.Connected, resume);
      room.off(RoomEvent.Reconnected, resume);
    };
  }, [room]);

  const blocked = !canPlayAudio || contextBlocked;

  const unlock = () => {
    // Resume synchronously, inside the gesture: Safari ignores a resume that
    // happens after an await, and startAudio reaches its own only later.
    const context = mixContext(room);
    if (context && context.state !== "running") void context.resume().catch(() => {});
    void startAudio().catch(() => {});
  };

  useEffect(() => {
    if (!blocked) return;
    // Capture phase: a handler that stops propagation must not swallow it.
    for (const type of GESTURES) document.addEventListener(type, unlock, { capture: true });
    return () => {
      for (const type of GESTURES) document.removeEventListener(type, unlock, { capture: true });
    };
    // `unlock` only closes over room and startAudio, both in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, startAudio, room]);

  return { blocked, unlock };
}
